import { api } from './client';

type Envelope<T> = { data: T };

export type OrganizationView = {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  brandColor: string;
  plan: string;
  customDomain: string | null;
  numberingFormat: string | null;
  numberingNextSeq: number;
};

export const organizationsApi = {
  async me(): Promise<OrganizationView> {
    const { data } = await api.get<Envelope<OrganizationView>>('/organizations/me');
    return data.data;
  },

  async update(input: {
    name?: string;
    brandColor?: string;
    logoUrl?: string | null;
    numberingFormat?: string | null;
    numberingNextSeq?: number;
  }): Promise<OrganizationView> {
    const { data } = await api.patch<Envelope<OrganizationView>>('/organizations/me', input);
    return data.data;
  },

  async previewNumbering(input: {
    format?: string;
    type?: string;
    dept?: string;
    form?: string;
  }): Promise<{ number: string }> {
    const { data } = await api.post<Envelope<{ number: string }>>(
      '/organizations/me/numbering/preview',
      input,
    );
    return data.data;
  },

  async listAudit(params: { page?: number; perPage?: number; action?: string; search?: string } = {}): Promise<AuditPage> {
    const { data } = await api.get<Envelope<AuditPage>>('/organizations/me/audit', {
      params: {
        page: params.page,
        per_page: params.perPage,
        action: params.action,
        search: params.search,
      },
    });
    return data.data;
  },

  async reportsOverview(): Promise<ReportsOverview> {
    const { data } = await api.get<Envelope<ReportsOverview>>('/organizations/me/reports/overview');
    return data.data;
  },

  async billing(): Promise<BillingUsage> {
    const { data } = await api.get<Envelope<BillingUsage>>('/organizations/me/billing');
    return data.data;
  },

  async adminDashboard(): Promise<AdminDashboard> {
    const { data } = await api.get<Envelope<AdminDashboard>>('/organizations/me/admin-dashboard');
    return data.data;
  },
};

export type AdminDashboard = {
  total: number;
  byType: Array<{ type: string; count: number }>;
  reviewNotDone: number;
  reviewDueSoon: number;
  nonConformingCount: number;
  nonConforming: Array<{ kind: string; key: string; values: string[] }>;
  unused: number;
  folders: Array<{ id: string; name: string; color: string; count: number }>;
};

export type BillingUsage = {
  plan: 'free' | 'business' | 'enterprise';
  trialEndsAt: string | null;
  periodStart: string;
  periodEnd: string;
  users: { used: number; limit: number };
  envelopes: { used: number; limit: number };
  bulkSend: { limit: number };
};

export type AuditEntry = {
  id: string;
  action: string;
  actorEmail: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  envelopeId: string | null;
  resourceType: string | null;
  resourceId: string | null;
  metadata: unknown;
  createdAt: string;
};

export type AuditPage = {
  items: AuditEntry[];
  total: number;
  page: number;
  perPage: number;
};

export type ReportsOverview = {
  byStatus: Array<{ status: string; count: number }>;
  topCreators: Array<{
    id: string;
    name: string;
    email: string;
    envelopeCount: number;
    completedCount: number;
  }>;
  monthly: Array<{ month: string; created: number; completed: number }>;
  avgResponseHours: number | null;
  windowDays: number;
};
